import React, { useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ComponentUsageInfo, ComponentUsageLink, groupUsageByBuilding } from './ComponentTypeUsageList';

export type { ComponentUsageInfo } from './ComponentTypeUsageList';

interface ComponentTypeUsageModalProps {
    show: boolean;
    onHide: () => void;
    componentTypeName: string;
    canDelete?: boolean;
    usage?: ComponentUsageInfo[];
    onConfirmDelete?: () => void;
    isDeleting?: boolean;
    isCheckingUsage?: boolean;
}

/**
 * Delete confirmation for a component type: blocks with the components that
 * still use it, or asks for confirmation when none do. Browsing the instances
 * is the inline "Instanser i byggnader" section on Komponenttyper.
 */
const ComponentTypeUsageModal: React.FC<ComponentTypeUsageModalProps> = ({
    show,
    onHide,
    componentTypeName,
    canDelete = false,
    usage,
    onConfirmDelete,
    isDeleting = false,
    isCheckingUsage = false,
}) => {
    const handleConfirm = () => {
        if (onConfirmDelete) {
            onConfirmDelete();
        }
    };

    const groupedUsage = useMemo(() => groupUsageByBuilding(usage ?? []), [usage]);
    const buildingCount = groupedUsage.length;

    // Show loading state while checking usage
    if (isCheckingUsage) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Radera komponenttyp</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex align-items-center justify-content-center py-4">
                        <span className="spinner-border spinner-border-sm me-3" />
                        <span>Söker i byggnader...</span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Avbryt
                    </Button>
                    <Button variant="danger" disabled>
                        Radera
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Cannot delete (in use)
    if (!canDelete && usage && usage.length > 0) {
        return (
            <Modal show={show} onHide={onHide} size="lg">
                <Modal.Header closeButton style={{ backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
                    <Modal.Title style={{ color: '#856404', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>&#x26A0;&#xFE0F;</span>
                        Kan inte radera komponenttyp
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                        Komponenttypen <strong>"{componentTypeName}"</strong> kan inte raderas eftersom den används av:
                    </p>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#e7f3ff',
                            borderLeft: '4px solid #2196F3',
                            borderRadius: '4px',
                        }}
                    >
                        <span style={{ fontSize: '24px' }}>&#x1F3E2;</span>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>
                            {usage.length} {usage.length === 1 ? 'komponent' : 'komponenter'} i {buildingCount} {buildingCount === 1 ? 'byggnad' : 'byggnader'}
                        </span>
                    </div>

                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
                        {groupedUsage.slice(0, 10).map((building) => {
                            const allComponents = building.levels.flatMap(l => l.rooms.flatMap(r => r.components));
                            const remainingCount = allComponents.length - 5;

                            return (
                                <div key={building.buildingKey} style={{ marginBottom: '16px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>
                                        {building.buildingName}
                                    </div>
                                    <ul style={{ marginLeft: '20px', marginBottom: '0' }}>
                                        {allComponents.slice(0, 5).map((comp) => (
                                            <li key={comp.componentKey} style={{ marginBottom: '4px', fontSize: '13px' }}>
                                                <ComponentUsageLink item={comp} />
                                            </li>
                                        ))}
                                        {remainingCount > 0 && (
                                            <li style={{ color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
                                                ... och {remainingCount} {remainingCount === 1 ? 'komponent' : 'komponenter'} till
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            );
                        })}
                        {groupedUsage.length > 10 && (
                            <div style={{ color: '#666', fontStyle: 'italic', fontSize: '14px', marginTop: '10px' }}>
                                ... och {groupedUsage.length - 10} {groupedUsage.length - 10 === 1 ? 'byggnad' : 'byggnader'} till
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            marginTop: '20px',
                        }}
                    >
                        <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                            <strong>Så här går du vidare:</strong> Byt komponenttyp på ovanstående komponenter innan du kan radera denna typ.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={onHide}>
                        OK, jag förstår
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Safe to delete (confirmation)
    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Bekräfta borttagning</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: '16px' }}>
                    Är du säker på att du vill radera komponenttypen <strong>"{componentTypeName}"</strong>?
                </p>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: 0 }}>
                    Denna åtgärd kan inte ångras.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isDeleting}>
                    Avbryt
                </Button>
                <Button variant="danger" onClick={handleConfirm} disabled={isDeleting}>
                    {isDeleting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Raderar...
                        </>
                    ) : (
                        'Radera'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ComponentTypeUsageModal;
